import {useCallback, useMemo, useRef, useState} from 'preact/hooks';

import type {AuthorizeIframeProps} from '~/components/AuthorizeIframe';
import {useBugsnag} from '~/foundation/Bugsnag/hooks';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {useAdjustedUxMode} from '~/hooks/useAdjustedUxMode';
import {useAuthorizeUrl} from '~/hooks/useAuthorizeUrl';
import {useDispatchEvent} from '~/hooks/useDispatchEvent';
import {useWindoidMessageHandler} from '~/hooks/useWindoidMessageHandler';
import type {SdkErrorCode} from '~/types/authorize';
import type {BuildAuthorizeUrlParams} from '~/types/authorizeUrlParams';
import type {
  AuthorizeErrorEvent,
  CompletedEvent,
  CustomFlowSideEffectEvent,
} from '~/types/event';
import type {IframeElement} from '~/types/iframe';
import {exchangeLoginCookie} from '~/utils/cookieExchange';
import {openWindoid} from '~/utils/windoid';

type UsePrequalAuthParams = BuildAuthorizeUrlParams & {
  handleLoginCompleted?: () => void;
  handlePrequalFlowSideEffect?: (event: CustomFlowSideEffectEvent) => void;
  setButtonLoading?: (loading: boolean) => void;
  storefrontOrigin?: string;
};

export const usePrequalAuth = ({
  handleLoginCompleted,
  handlePrequalFlowSideEffect,
  setButtonLoading,
  storefrontOrigin,
  uxMode = 'iframe',
  ...props
}: UsePrequalAuthParams) => {
  const {notify} = useBugsnag();
  const {trackUserAction} = useMonorail();
  const dispatchEvent = useDispatchEvent();
  const [error, setError] = useState<SdkErrorCode>();

  const iframeRef = useRef<IframeElement | null>(null);
  const windoidRef = useRef<WindowProxy | null>(null);
  const adjustedUxMode = useAdjustedUxMode({
    defaultUxMode: 'iframe',
    uxMode,
  });

  const {authorizeUrl} = useAuthorizeUrl({
    analyticsContext: 'loginWithShopPrequal',
    error,
    ...props,
    uxMode: adjustedUxMode,
    disableSignUp: false,
    flow: 'prequal',
    flowVersion: 'unspecified',
    hideCopy: false,
    proxy: true,
  });

  const handleComplete = useCallback(
    async ({loggedIn, shouldFinalizeLogin}: CompletedEvent) => {
      if (loggedIn && shouldFinalizeLogin) {
        await exchangeLoginCookie(storefrontOrigin, notify);
      }
      handleLoginCompleted?.();
    },
    [handleLoginCompleted, notify, storefrontOrigin],
  );

  const onError = useCallback(
    ({code, email, message}: AuthorizeErrorEvent): void => {
      dispatchEvent('error', {code, email, message});

      if (code === 'retriable_server_error') {
        if (error === code) {
          iframeRef.current?.reload();
        }
        setError(code);
      }
    },
    [dispatchEvent, error],
  );

  const handlePromptContinue = useCallback(() => {
    trackUserAction({userAction: 'SIGN_IN_WITH_SHOP_PROMPT_CONTINUE_CLICK'});

    iframeRef?.current?.close({
      dismissMethod: 'windoid_continue',
      reason: 'user_prompt_continue_clicked',
    });
  }, [trackUserAction]);

  const handleWindoidMessage = useWindoidMessageHandler({
    handleComplete,
    handleCustomFlowSideEffect: handlePrequalFlowSideEffect,
    handleError: onError,
    windoidRef,
  });

  const startAuth = useCallback(() => {
    trackUserAction({userAction: 'SIGN_IN_WITH_SHOP_BUTTON_CLICK'});
    if (adjustedUxMode === 'windoid') {
      if (!authorizeUrl) {
        notify(new Error('Cannot open windoid: authorizeUrl is not defined'));
        return;
      }
      windoidRef.current = openWindoid(authorizeUrl, handleWindoidMessage);
    } else {
      iframeRef.current?.open('user_button_clicked');
    }
  }, [
    adjustedUxMode,
    authorizeUrl,
    handleWindoidMessage,
    notify,
    trackUserAction,
  ]);

  const authorizeIframeProps = useMemo<AuthorizeIframeProps | null>(() => {
    if (adjustedUxMode !== 'iframe') {
      return null;
    }

    return {
      insideModal: true,
      onComplete: handleComplete,
      onCustomFlowSideEffect: handlePrequalFlowSideEffect,
      onError,
      onPromptContinue: handlePromptContinue,
      proxy: true,
      ref: iframeRef,
      src: authorizeUrl,
      variant: 'default' as const,
    };
  }, [
    adjustedUxMode,
    authorizeUrl,
    handleComplete,
    handlePrequalFlowSideEffect,
    handlePromptContinue,
    onError,
  ]);

  return {
    authorizeIframeProps,
    iframeRef,
    startAuth,
    windoidRef,
  };
};
